import { OptionsApi } from '@core/swagger-client';
import { ContainerDefault, ErrorMessagePage, Heading, LoaderPage } from '@core/ui';
import { BackLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const WebinarDurationSettingsPage = () => {
    const { t } = useTranslation();
    const [maxDuration, setMaxDuration] = useState('01:00');
    const [durationBeforeStart, setDurationBeforeStart] = useState('00:15');
    const [durationAfterEnd, setDurationAfterEnd] = useState('00:15');
    const [totalDuration, setTotalDuration] = useState('');

    const { data: tiers, isLoading: tiersIsLoading, error: tiersError } = useQuery(OptionsApi.tier.getAvailable());

    useEffect(() => {
        if (!tiers) {
            return;
        }

        const maxDuration = Duration.fromISOTime(tiers.length > 0 ? tiers[0].duration : '01:00:00');
        //setMaxDuration(maxDuration.toFormat('hh:mm'));
        setMaxDuration(maxDuration.rescale().toHuman({ unitDisplay: 'narrow' }));

        const durationBeforeStart = Duration.fromISOTime(tiers.length > 0 ? tiers[0].durationBeforeStart : '00:15:00');
        setDurationBeforeStart(durationBeforeStart.rescale().toHuman({ unitDisplay: 'narrow' }));

        const durationAfterEnd = Duration.fromISOTime(tiers.length > 0 ? tiers[0].durationAfterEnd : '00:15:00');
        setDurationAfterEnd(durationAfterEnd.rescale().toHuman({ unitDisplay: 'narrow' }));

        const totalDuration = maxDuration.plus(durationBeforeStart).plus(durationAfterEnd);
        setTotalDuration(totalDuration.rescale().toHuman({ unitDisplay: 'narrow' }));
    }, [tiers]);

    if (tiersError) {
        return <ErrorMessagePage apiError={tiersError} />;
    }

    if (tiersIsLoading) {
        return <LoaderPage />;
    }

    return (
        <ContainerDefault>
            <BackLink to="/manage">{t('Button.BackToManageEnv')}</BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('WebinarDurationSettings.PageHeading')}
            </Heading>

            <p className="max-w-2xl bg-white rounded-lg grid grid-cols-[1fr_auto] items-end shadow-4xl mb-10 p-2">
                <span className="p-2">{t('WebinarDurationSettings.Text.DurationBeforeStart')}</span>{' '}
                <span className="p-2 text-right">{durationBeforeStart}</span>
                <span className="p-2">{t('WebinarDurationSettings.Text.MaxDuration')}</span> <span className="p-2 text-right">{maxDuration}</span>
                <span className="border-b border-gray-300 p-2">{t('WebinarDurationSettings.Text.DurationAfterEnd')}</span>{' '}
                <span className="border-b border-gray-500 p-2 text-right">{durationAfterEnd} </span>
                <span className="p-2">{t('WebinarDurationSettings.Text.TotalDuration')}</span>{' '}
                <span className="font-medium p-2 text-right">{totalDuration}</span>
            </p>
            <div className="max-w-4xl mb-10 prose">
                <Trans components={{ ul: <ul />, li: <li /> }} i18nKey="WebinarDurationSettings.InfoText" />
            </div>
            <div className="max-w-4xl grid grid-cols-[minmax(3rem,_20%)_1fr_minmax(3rem,_20%)] mt-10">
                <span className="col-span-full border-x border-gray-500 font-medium mb-1 px-2 py-1 text-center">
                    {t('WebinarDurationSettings.Figure.ChannelInUse')}
                </span>
                <div className="h-4 row-start-2 bg-accent rounded-l"></div>
                <div className="h-4 col-start-2 bg-primary"></div>
                <div className="h-4 col-start-3 bg-accent rounded-r"></div>
                <span className="row-start-3 col-start-1 border-l border-gray-500 mt-1 px-2 py-1 text-center">
                    {t('WebinarDurationSettings.Figure.ChannelAllocation')}
                </span>
                <span className="row-start-4 col-start-2 border-l border-gray-500 pt-1 px-2 text-left">
                    {t('WebinarDurationSettings.Figure.PlannedStartTime')}
                </span>
                <span className="row-start-3 col-start-2 border-x border-gray-500 mt-1 px-2 py-1 text-center">
                    {t('WebinarDurationSettings.Figure.PlannedDuration')}
                </span>
                <span className="row-start-5 col-start-2 pt-1 px-2 text-right">{t('WebinarDurationSettings.Figure.PlannedEndTime')}</span>
                <span className="row-start-4 row-span-2 col-start-2 border-r border-gray-500"></span>
                <span className="row-start-3 col-start-3 border-r border-gray-500 mt-1 px-2 py-1 text-center">
                    {t('WebinarDurationSettings.Figure.RunOutTime')}
                </span>
            </div>
        </ContainerDefault>
    );
};
